import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Syringe, Search, ChartNoAxesCombined, X, Info } from 'lucide-react';
import { medications } from '../../../data/medications.js';
import { eracsPrep } from '../../../data/hospitals/thiEracs';
import CaseSetup from './CaseSetup';

const THICaseSetupGuide = ({
  onClose,
  hospital,
  activeSegment,
  activeCardiacTab,
  setActiveCardiacTab,
  activeVascularTab,
  setActiveVascularTab,
  activeTransplantTab,
  setActiveTransplantTab,
  activeCathLabTab,
  setActiveCathLabTab,
  activeSpecialtyTab,
  onSegmentChange,
  onOpenEnhancedRecovery
}) => {
  const [expandedCategories, setExpandedCategories] = useState({});
  const [drugSearchQuery, setDrugSearchQuery] = useState('');
  const [locationFilter, setLocationFilter] = useState('all');
  const [showLegend, setShowLegend] = useState(true);
  const [selectedMedication, setSelectedMedication] = useState(null);

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  // Medications are now imported from external data file

  // Standard Cardiac Drug Setup Category
  const standardCardiacDrugSetup = {
    id: 'standard-cardiac-drugs',
    name: 'Standard Cardiac Drug Setup',
    color: 'from-[#9F8D70] to-[#8a7a61]',
    icon: Syringe,
    description: 'Baseline medications for most cardiac cases',
    sections: [
      {
        name: 'Pre-Medications',
        drugs: ['Midazolam 2mL or 5mL']
      },
      {
        name: 'Induction Drugs',
        drugs: [
          'Propofol 20mL or Etomidate (choose one)',
          'Lidocaine 2%',
          'Rocuronium, Vecuronium, and/or Succinylcholine',
          'Fentanyl'
        ]
      },
      {
        name: 'Anti-Fibrinolytic & Anticoagulation',
        drugs: [
          {
            main: 'Antifibrinolytic (choose one)',
            sub: [
              'Tranexamic Acid (TXA) 10 mg/kg bolus',
              'Aminocaproic Acid (Amicar) 10g bolus'
            ]
          },
          'Heparin 300-400 IU/kg'
        ]
      },
      {
        name: 'Antibiotics',
        drugs: [
          'Vancomycin 15mg/kg',
          'Cefepime 2 grams'
        ]
      },
      {
        name: 'Emergency Drug Syringes',
        drugs: [
          {
            main: 'Uppers',
            sub: [
              'Phenylephrine 100 mcg/mL',
              'Ephedrine 5 mg/mL',
              'Norepinephrine 16 mcg/mL',
              'Epinephrine (Low-dose) 16 mcg/mL',
              'Epinephrine (Code-dose) 100 mcg/mL',
              'Calcium Chloride 100 mg/mL'
            ]
          },
          {
            main: 'Downers',
            sub: [
              'Esmolol 10 mg/mL',
              'Nitroglycerin 100 mcg/mL',
              '+/- Nicardipine 100 mcg/mL or 250 mcg/mL'
            ]
          }
        ]
      },
      {
        name: 'Infusions',
        drugs: [
          {
            main: 'Carrier + Antifibrinolytic',
            sub: [
              '500 mL NS run at 50 mL/hr',
              'If TXA, add 2-3 grams to infuse 3 mg/kg/hr',
              'If Amicar, add 10 grams to infuse 1 gram/hr'
            ]
          },
          'Norepinephrine 8 mg in 250mL (32 mcg/mL)',
          '+/- Epinephrine 4 mg in 250mL (16 mcg/mL)',
          '+/- Vasopressin 40 units in 100mL (0.4 units/mL)',
          '+/- Dobutamine',
          '+/- Dexmedetomidine 400 mcg in 100mL - attending preference'
        ]
      }
    ]
  };


  // Filter medications for Drug Finder
  const filteredMedications = medications.filter(med => {
    const matchesSearch = drugSearchQuery === '' ||
      med.genericName.toLowerCase().includes(drugSearchQuery.toLowerCase()) ||
      (med.proprietaryName && med.proprietaryName.toLowerCase().includes(drugSearchQuery.toLowerCase()));

    const matchesLocation = locationFilter === 'all' || med.locations.includes(locationFilter);

    return matchesSearch && matchesLocation;
  });

  const consolidatedMedications = filteredMedications;

  // Get unique locations for filter buttons
  const allLocations = [...new Set(medications.flatMap(m => m.locations))].sort();
  const locations = ['all', ...allLocations];

  return (
    <div className="space-y-4">
      {/* DRUG FINDER SEGMENT */}
      {activeSegment === 'drug-finder' && (
        <div className="space-y-4">
          {/* About this tool + where drugs live. Collapses when a search begins. */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-[#9F8D70]/30 dark:border-[#9F8D70]/50 shadow-card overflow-hidden">
            <button
              onClick={() => setShowLegend(!showLegend)}
              className="w-full px-4 py-3 bg-[#9F8D70]/10 dark:bg-[#9F8D70]/20 flex items-center justify-between text-left"
            >
              <span className="font-semibold text-[#8a7a61] dark:text-[#C4B5A0]">About this tool</span>
              {showLegend ? <ChevronUp size={18} className="text-[#9F8D70]" /> : <ChevronDown size={18} className="text-[#9F8D70]" />}
            </button>
            {showLegend && (
              <div className="p-4 space-y-4">
                <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                  Medications at THI are located across different areas of the CVOR and hospital. Some are stocked in the CVOR A Cart (Pyxis in each OR), others in Core Pyxis 1 or 2, and some require compounding or delivery by the pharmacy. Use this tool to quickly locate medications and check whether they come pre-compounded or need to be drawn up.
                </p>
                <div className="grid grid-cols-1 gap-3 text-sm">
                  <div>
                    <p className="eyebrow mb-1.5">CVOR</p>
                    <ul className="space-y-1 text-gray-700 dark:text-gray-300">
                      <li><span className="font-medium text-gray-900 dark:text-white">CVOR A Cart:</span> Pyxis in each OR</li>
                      <li><span className="font-medium text-gray-900 dark:text-white">Core Pyxis 1:</span> near CVOR 10 · has fridge</li>
                      <li><span className="font-medium text-gray-900 dark:text-white">Core Pyxis 2:</span> center of core · no fridge</li>
                    </ul>
                  </div>
                  <div>
                    <p className="eyebrow mb-1.5">Cath Lab</p>
                    <ul className="space-y-1 text-gray-700 dark:text-gray-300">
                      <li><span className="font-medium text-gray-900 dark:text-white">Cath Lab A Cart:</span> Pyxis in each room</li>
                      <li><span className="font-medium text-gray-900 dark:text-white">Cath Lab Core Pyxis:</span> central core Pyxis</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Search Interface */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-card overflow-hidden shadow-sm">
            <div className="p-5 bg-gray-50 dark:bg-gray-800">
              {/* Search Bar */}
              <div className="mb-3">
              <div className="relative">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                <input
                  type="text"
                  value={drugSearchQuery}
                  onChange={(e) => {
                    setDrugSearchQuery(e.target.value);
                    // Auto-collapse legend when user starts searching
                    if (e.target.value.length > 0 && showLegend) {
                      setShowLegend(false);
                    } else if (e.target.value.length === 0 && !showLegend) {
                      setShowLegend(true);
                    }
                  }}
                  placeholder="Find a medication"
                  className="w-full pl-10 pr-4 py-2.5 text-[15px] rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-600"
                />
              </div>
              {/* Results Count Badge */}
              {drugSearchQuery && (
                <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  {consolidatedMedications.length} result{consolidatedMedications.length !== 1 ? 's' : ''}
                </div>
              )}
            </div>

            {/* Location Filters - Horizontal Scroll */}
            <div className="mb-3">
              <div className="flex gap-2 overflow-x-auto pb-2" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                {locations.map(loc => (
                  <button
                    key={loc}
                    onClick={() => setLocationFilter(loc)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all flex-shrink-0 ${
                      locationFilter === loc
                        ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900 shadow-sm'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {loc === 'all' ? 'All Locations' : loc}
                  </button>
                ))}
              </div>
            </div>

            {/* Results */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              {consolidatedMedications.length === 0 ? (
                <p className="p-4 text-gray-500 dark:text-gray-400 text-center">No medications found</p>
              ) : (
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {consolidatedMedications.map((med, idx) => (
                    <button
                      key={idx}
                      onClick={() => med.clinicalNotes ? setSelectedMedication(med) : null}
                      className={`w-full p-4 text-left ${med.clinicalNotes ? 'hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors' : ''}`}
                    >
                      <div className="flex gap-4">
                        <div className="flex-1 min-w-0 flex flex-col justify-between">
                          <div>
                            <h4 className="font-bold text-gray-900 dark:text-white flex items-center gap-1">
                              {med.genericName}
                              {med.clinicalNotes && (
                                <Info size={16} className="text-gray-400 flex-shrink-0" />
                              )}
                            </h4>
                            <div className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                              {med.containerSize} {med.containerType} • {med.concentration}
                            </div>
                          </div>
                          <div className="mt-3 space-y-1">
                            <div className="text-sm text-gray-700 dark:text-gray-300">
                              <span className="font-medium text-emerald-700 dark:text-emerald-400">Preparation:</span> {med.preparation}
                            </div>
                            {med.epicOrderRequired && (
                              <div>
                                <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">
                                  Epic Order Required
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                          {med.locations.map((location, locIdx) => (
                            <span key={locIdx} className="inline-block px-2 py-1 rounded text-xs font-medium bg-[#9F8D70]/15 dark:bg-[#9F8D70]/25 text-[#6b5d48] dark:text-[#C4B5A0] whitespace-nowrap">
                              {location}
                            </span>
                          ))}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        </div>
      )}

      {/* CASE SETUP: every case type, driven by data/hospitals/thiCases.js */}
      {activeSegment !== 'drug-finder' && (
        <CaseSetup
          category={activeSegment}
          onCategoryChange={onSegmentChange}
          onAction={(id) => { if (id === 'eras') onOpenEnhancedRecovery?.(); }}
          extras={activeSegment === 'cardiac' && (
            <>
      {/* Standard Cardiac Drug Setup */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border-2 border-[#9F8D70]/30 dark:border-[#9F8D70]/50 overflow-hidden shadow-lg">
        <button
          onClick={() => toggleCategory(standardCardiacDrugSetup.id)}
          className="w-full p-5 bg-[#9F8D70]/10 dark:bg-[#9F8D70]/20 hover:bg-[#9F8D70]/15 dark:hover:bg-[#9F8D70]/25 transition-all flex items-center justify-between"
        >
          <div className="flex items-center gap-3">
            <Syringe size={24} className="text-[#9F8D70] dark:text-[#C4B5A0]" />
            <div className="text-left">
              <h3 className="font-bold text-lg text-[#9F8D70] dark:text-[#C4B5A0]">{standardCardiacDrugSetup.name}</h3>
              <p className="text-sm text-gray-700 dark:text-gray-300 mt-0.5">{standardCardiacDrugSetup.description}</p>
            </div>
          </div>
          {expandedCategories[standardCardiacDrugSetup.id] ? (
            <ChevronUp size={24} className="flex-shrink-0 text-[#9F8D70] dark:text-[#C4B5A0]" />
          ) : (
            <ChevronDown size={24} className="flex-shrink-0 text-[#9F8D70] dark:text-[#C4B5A0]" />
          )}
        </button>

        {expandedCategories[standardCardiacDrugSetup.id] && (
          <div className="border-t-2 border-gray-200 dark:border-gray-700 p-6">
            {standardCardiacDrugSetup.sections.map((section, idx) => (
              <div key={idx} className={idx > 0 ? 'mt-5' : ''}>
                <h5 className="font-semibold text-gray-900 dark:text-white mb-2 text-sm">
                  {section.name}
                  {section.subtitle && <span className="text-gray-500 dark:text-gray-400 font-normal"> ({section.subtitle})</span>}
                </h5>
                <ul className="space-y-1 ml-4">
                  {section.drugs.map((drug, drugIdx) => {
                    // Handle nested structure for items with sub-bullets
                    if (typeof drug === 'object' && drug.main) {
                      return (
                        <li key={drugIdx} className="text-gray-700 dark:text-gray-300 text-sm">
                          <div className="font-medium">{drug.main}</div>
                          <ul className="ml-4 mt-1 space-y-0.5">
                            {drug.sub.map((subItem, subIdx) => (
                              <li key={subIdx} className="text-gray-600 dark:text-gray-400 text-sm">
                                • {subItem}
                              </li>
                            ))}
                          </ul>
                        </li>
                      );
                    }
                    // Handle simple string items
                    return (
                      <li key={drugIdx} className="text-gray-700 dark:text-gray-300 text-sm">
                        {drug}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ERACS: only what to have prepared; the full pathway lives in Enhanced Recovery */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-card overflow-hidden">
        <div className="p-4 flex items-center gap-3 border-b border-gray-100 dark:border-gray-700/70">
          <ChartNoAxesCombined size={22} className="text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white">ERACS: have ready</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">When the patient is on the enhanced recovery pathway</p>
          </div>
        </div>
        <ul className="px-4 py-3 space-y-1.5">
          {eracsPrep.map((item) => (
            <li key={item} className="text-sm text-gray-700 dark:text-gray-300 leading-snug pl-3 relative before:content-[''] before:absolute before:left-0 before:top-[0.55em] before:w-1 before:h-1 before:rounded-full before:bg-gray-400">
              {item}
            </li>
          ))}
        </ul>
        {onOpenEnhancedRecovery && (
          <button
            onClick={onOpenEnhancedRecovery}
            className="w-full px-4 py-3 border-t border-gray-100 dark:border-gray-700/70 text-sm font-medium text-emerald-700 dark:text-emerald-400 text-left flex items-center justify-between"
          >
            Full pathway by procedure type
            <span aria-hidden="true">›</span>
          </button>
        )}
      </div>

            </>
          )}
        />
      )}

      {/* Clinical Notes Modal */}
      {selectedMedication && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => setSelectedMedication(null)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-2xl w-full max-h-[80vh] overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-[#9F8D70] to-[#8a7a61] p-5 flex items-start justify-between">
              <div className="flex-1">
                <h3 className="font-bold text-xl text-white">{selectedMedication.genericName}</h3>
                <p className="text-white/80 text-sm mt-1">
                  {selectedMedication.containerSize} {selectedMedication.containerType} • {selectedMedication.concentration}
                </p>
              </div>
              <button
                onClick={() => setSelectedMedication(null)}
                className="text-white hover:bg-white/15 rounded-lg p-2 transition-colors flex-shrink-0"
              >
                <X size={24} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-140px)]">
              <div className="space-y-4">
                {/* Preparation Info */}
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Preparation</h4>
                  <p className="text-gray-700 dark:text-gray-300">{selectedMedication.preparation}</p>
                </div>

                {/* Locations */}
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Locations</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedMedication.locations.map((location, idx) => (
                      <span key={idx} className="inline-block px-3 py-1.5 rounded-lg text-sm font-medium bg-[#9F8D70]/15 dark:bg-[#9F8D70]/25 text-[#6b5d48] dark:text-[#C4B5A0]">
                        {location}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Epic Order Required */}
                {selectedMedication.epicOrderRequired && (
                  <div className="bg-red-50 dark:bg-red-900/20 border-2 border-red-200 dark:border-red-700 rounded-lg p-4">
                    <p className="font-semibold text-red-700 dark:text-red-300">
                      ⚠️ Epic Order Required
                    </p>
                  </div>
                )}

                {/* Clinical Notes */}
                {selectedMedication.clinicalNotes && (
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Clinical Notes & Instructions</h4>
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                      <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
                        {selectedMedication.clinicalNotes}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default THICaseSetupGuide;
